import React, { useEffect, useState, useRef, useContext } from "react";
import { useHistory } from "react-router";
import { useReactToPrint } from "react-to-print";
import { generatePath } from "react-router-dom";
import posthog from "posthog-js";
import { CB_EVENTS, CB_CHANNELS } from "campaign-builder/analytics/cbAnalytics";
import { SnackBarContext } from "components/snack-bar";
import Box from "@material-ui/core/Box";
import { COUPON_CODE_ERROR_MESSAGE, CAMPAIGN_INTERNAL_STATUSES, EDDM_NAVIGATION, NAVIGATION } from "pages/constants";
import { ProgressBarContext } from "components/progress-bar";
import { decrypt } from "pages/auth/utils";
import { addBusinessDays } from "components/date-picker/utils/controlDate";
import { getCurrencyCode, gtagWrapper } from "utils";
import { useGetFlyerInfo } from "pages/flyer/flyerTypePage/useGetFlyerInfo";
import CheckoutContent from "campaign-builder/checkout/CheckoutContent";
import { useBillingPreview } from "campaign-builder/checkout/hooks/useBillingPreview";
import { EddmOadsSidebar } from "campaign-builder/EddmOadsSidebar";
import { FeatureFlagContext } from "context/featureFlagContext";
import { deriveCheckoutDiscountChoice } from "campaign-builder/submit/deriveCheckoutDiscountChoice";
import { useReviewDiscountSummary } from "campaign-builder/channels/directmail/adm/order-details/reviewOffer";
import EddmReviewOffer from "pages/eddm/submit/EddmReviewOffer";
import Header from "components/header";
import { DM_MIN_SELECTABLE_DATE } from "../../../constants";
import { EDDM_ROUTING } from "../../../config/routing";
import QuoteView from "pages/eddm/submit/quote/QuoteView";
import { formatDate } from "../../../utils/date";
import { calculateDates, convertNumber, createPrintingPage } from "utils/campaignQuote";
import BounceLoader from "../../../components/loaders/bounce-loader";
import { apiClient } from "../../../module/api";
import { CAMPAIGN_ENUM_TYPES, CAMPAIGN_LOG_ENUM_TYPES, CAMPAIGN_SUBTYPES_ENUM } from "../../../shared/constants";
import useExitPrompt from "../../../Hooks/useExitPrompt";
import { useEDDMCostsCalculation } from "../../../Hooks/useEDDMCostsCalculation";
import {
  updateCampaignExtraData,
  updateSelfServeCampaignData,
  updateCampaign as updateCampaignDB,
  getCampaignFlyerData,
  getEddmCampaignCost,
  doCheckoutGQL,
} from "../../../graphQL";
import { calculateTaxAndTotalCost } from "../../../Hooks/useCalculating/utils";
import { useStore } from "../../../store";
import { insertCampaignLogs } from "../../../Logs/campaign/gql";
import { replaceNullValue } from "../../../Logs/campaign/utils";
import { toFixed2 } from "../../../utils/formators";

export const EDDMSubmitContainer = () => {
  const {
    country,
    costsCalculationData,
    client,
    user,
    timezone,
    campaign: {
      id: campaignId,
      submittedBy,
      quote,
      stripeCouponCode,
      isPaymentSkipped,
      startDate: savedStartDate = null,
      updatedAt,
      flyerType,
      campaignName,
      channel,
      internalStatus,
      isDM,
      couponType,
      staticCouponCode,
      dynamicCouponCode,
      isSubmitted,
      subtype,
      reviewMeetingDate,
    },
    city,
    updateCampaign,
    updateCostsCalculationData,
  } = useStore();

  const { recalculateEDDMCampaignCost } = useEDDMCostsCalculation();

  const [firstPageRef, secondPageRef] = [useRef(), useRef()];
  const history = useHistory();
  useExitPrompt(false);

  const [{ startDate }, setDates] = useState({ startDate: {} });
  const [data, setData] = useState({});
  const [isLoaded, setIsLoaded] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSubmitConfirmationModalOpen, setIsSubmitConfirmationModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [gtagEventSent, setGtagEventSent] = useState(false);
  const [couponErrorMessage, setCouponErrorMessage] = useState("");
  const [showCheckout, setShowCheckout] = useState(false);
  const promotionIdRef = useRef("");
  const customerIdRef = useRef("");

  // HUB-440 (EDDM review-for-discount): the drawer/quote review-offer's
  // "one discount per order" choice, lifted from <EddmReviewOffer>. Fed into
  // deriveCheckoutDiscountChoice below so the legacy checkout carries the
  // client's intent as `discount_choice`. `null` until the offer's first
  // effect runs (treated the same as no discount).
  const [reviewDiscountChoice, setReviewDiscountChoice] = useState(null);
  // Lifted from <EddmReviewOffer> so the quote can show the discount line.
  const [reviewEntitlement, setReviewEntitlement] = useState(null);
  const { featureFlags, loading: flagsLoading } = useContext(FeatureFlagContext);
  const isReviewDiscountEnabled = !flagsLoading && !!featureFlags.isReviewDiscountEnabled;

  const runSnackBar = useContext(SnackBarContext);
  const runProgressBar = useContext(ProgressBarContext);
  const { pageState, isLoading, flyerInfo } = useGetFlyerInfo();
  const { detailedCost, settings, flyersAmount, costPerFlyer } = costsCalculationData;
  const { totalCost, tax, subtotal, amountOff, percentOff } = detailedCost;
  const { countryTaxRate } = settings;

  const clientId = client.id;
  const userId = user.id;

  // PAD-32157 — EDDM Submit step milestone. Fires once per campaign per browser
  // session — dedup via sessionStorage so a user who navigates away from
  // /submit and returns to the same campaign doesn't inflate funnel counts
  // (an in-memory ref would reset on remount). Gate on totalCost > 0 so a
  // direct refresh doesn't record 0 before recalculateEDDMCampaignCost runs.
  // Parallel to legacy EDDM submit events (Clicked Campaign Submit Button, etc.)
  // which stay untouched. Cleanup: unregister channel on unmount so
  // `channel: 'EDDM'` doesn't leak into non-EDDM events emitted later.
  //
  // The authoritative post-payment amount is captured separately in
  // `CB - Payment Confirmed.amount` (from the Stripe callback).
  useEffect(() => {
    posthog.register({ channel: CB_CHANNELS.EDDM });
    return () => {
      posthog.unregister("channel");
    };
  }, []);
  useEffect(() => {
    if (!campaignId || !clientId) return;
    if (!totalCost || totalCost <= 0) return;
    const dedupKey = `cb-submit-reached:${campaignId}`;
    try {
      if (window.sessionStorage.getItem(dedupKey) === "1") return;
      window.sessionStorage.setItem(dedupKey, "1");
    } catch (_e) {
      // sessionStorage unavailable (private mode / disabled) — fall through
      // to emit; a duplicate is preferable to a missing milestone.
    }
    posthog.capture(CB_EVENTS.SUBMIT_REACHED, {
      campaign_id: campaignId,
      client_id: clientId,
      total_cost: totalCost,
    });
  }, [campaignId, clientId, totalCost]);

  let campaignDetailedCosts = {
    tax,
    subtotal,
    totalCost,
    amountOff: amountOff || 0,
    percentOff: percentOff || 0,
  };
  const currencySign = country?.currencySign || "$";

  // Server-authoritative breakdown via the shared
  // `useBillingPreview` hook. EDDM's FE math (`subtotal * 2.99% + 0.30` fee
  // and `detailedCost.tax` from a country-flat-rate calculator) diverges
  // from `/billing/charges/initiate` which uses TaxJar per-zip aggregation
  // and per-country gross-up — drawer must show what Stripe actually
  // charges.
  const currency = country ? getCurrencyCode(country) : "USD";
  // HUB-440: EDDM checks out through the OADS /billing flow (CheckoutContent),
  // NOT the legacy embedded checkout, so the review discount has to ride the
  // billing preview + the charge — exactly like ADM's useADMSubmit. Without it
  // the offer row says "applied" while the breakdown, the total and the Stripe
  // charge all stay at full price.
  // HUB-440: the quote's own FE math has no notion of the review discount, so
  // derive the same summary ADM's drawer renders (label + amount off) and feed
  // it to QuoteView for the discount line + reduced total.
  const reviewDiscount = useReviewDiscountSummary({
    entitlement: reviewEntitlement,
    subtotal,
    isReviewDiscountChosen: reviewDiscountChoice === "review",
  });
  const applyReviewDiscount =
    deriveCheckoutDiscountChoice({
      isReviewDiscountEnabled,
      discountChoice: reviewDiscountChoice,
      hasStripeCoupon: !!stripeCouponCode,
    }) === "review";
  const { costBreakdown, breakdownReady } = useBillingPreview({
    subtotal,
    currency,
    campaignId,
    couponCode: stripeCouponCode,
    applyReviewDiscount,
    showCheckout,
    onError: () => {
      runSnackBar({
        type: "error",
        msg: "Couldn't load price breakdown. Please try again.",
        vertical: "bottom",
        horizontal: "right",
      });
    },
  });

  const minStartDate = addBusinessDays(new Date(), DM_MIN_SELECTABLE_DATE, timezone);
  // const maxEndDate = addDaysWithTimezone(new Date(), 60, timezone);

  const runSideEffect = async () => {
    await updatePathfinderData();
    await updateCampaignAndCampaignExtraData();
    if (!isPaymentSkipped) {
      setIsModalOpen(false);
      setShowCheckout(true);
    } else {
      await sideEffects({ saveLogs: false, doCheckout: true });
    }
  };

  useEffect(() => {
    if (!isSubmitted) {
      recalculateEDDMCampaignCost();
    }
  }, [isSubmitted]);

  useEffect(() => {
    /**
     * when refresh the submit page or reenter to submit page, we fetch the coupon code from the db.
     * but promotionIdRef.current is empty
     * so we should validate coupon code once again before submit campaign.
     * and should update promotion id and customer id to send to stripe
     */
    if (
      isLoaded &&
      internalStatus === CAMPAIGN_INTERNAL_STATUSES.DRAFT &&
      stripeCouponCode &&
      !promotionIdRef.current
    ) {
      handleApplyCouponCode({ stripeCouponCode });
    }
  }, [isLoaded, stripeCouponCode, internalStatus]);

  useEffect(() => {
    /**
     * This is for really rare case.
     * we submit campaign with coupon and don't click pay button on stripe page.
     * i.e. the campaign is still draft status but coupon was already applied
     * then we skipped payment on platform for this campaign and we don't show the coupon code
     * so we need to remove coupon effect
     */
    if (isPaymentSkipped && stripeCouponCode && internalStatus === CAMPAIGN_INTERNAL_STATUSES.DRAFT) {
      updateCouponCode({ stripeCouponCode: null, amountOff: null, percentOff: null });

      // to do : should we also remove coupon code from mongodb when we skipped payment?
    }
  }, [isPaymentSkipped, stripeCouponCode, internalStatus]);

  useEffect(() => {
    if (history?.location?.search?.split("payment=")[1]) {
      const decryptedCampaignId = decrypt(history?.location?.search.split("payment=")[1]);
      window.history.replaceState(null, null, `${window.location.href.split("?payment")[0]}`);
      if (decryptedCampaignId === campaignId) {
        return sideEffects({ saveLogs: true, doCheckout: false });
      } else {
        runSnackBar({
          type: "error",
          msg: `Payment error.`,
          vertical: "bottom",
          horizontal: "right",
        });
      }
    }
  }, [history?.location?.search]);

  useEffect(() => {
    setIsLoaded(false);
    // Parse savedStartDate string to Date object for date-fns compatibility
    const parsedStartDate = savedStartDate ? new Date(savedStartDate) : null;
    const isValidDate = parsedStartDate && !isNaN(parsedStartDate.getTime());
    const dates = calculateDates({
      value: isValidDate ? parsedStartDate : null,
      minStartDate,
    });
    setDates(dates);

    let newInernalStatus = internalStatus;
    if (history?.location?.search?.split("payment=")[1]) {
      const decryptedCampaignId = decrypt(history?.location?.search.split("payment=")[1]);
      if (decryptedCampaignId === campaignId) {
        newInernalStatus = CAMPAIGN_INTERNAL_STATUSES.IN_REVIEW;
      }
    }

    const newCampaignData = {
      submitter: `${user.firstName} ${user.lastName}`,
      submittedBy,
      clientName: user?.clientName || client?.name || "",
      campaignName: campaignName ? campaignName.trim() : campaignName,
      channel,
      flyerType,
      currencySign,
      updatedAt: updatedAt || new Date(),
      internalStatus: newInernalStatus,
      isSubmitted: newInernalStatus !== CAMPAIGN_INTERNAL_STATUSES.DRAFT,
      quote: quote ? [...new Array(5 - String(quote).length).fill("0"), String(quote)].join("") : 0,
      country,
      amountOff,
      percentOff,
      grand: totalCost,
      couponDiscount: amountOff || 0,
      isDM: isDM,
      flyersAmount: flyersAmount,
      costPerFlyer: costPerFlyer,
      couponType,
      staticCouponCode,
      dynamicCouponCode,
      reviewMeetingDate,
    };
    setData(newCampaignData);

    setIsLoaded(true);
  }, [
    quote,
    stripeCouponCode,
    amountOff,
    percentOff,
    internalStatus,
    isDM,
    flyersAmount,
    costPerFlyer,
    couponType,
    staticCouponCode,
    dynamicCouponCode,
  ]);

  useEffect(() => {
    if (!gtagEventSent && isModalOpen && startDate) {
      gtagWrapper({
        event: "ss_submit_started",
        client_id: clientId.toString(),
        user_id: userId,
        campaign_id: campaignId,
        launch_date: formatDate(new Date(startDate.value), "MMM d, yyyy"),
        nr_of_flyers: flyersAmount,
        flyer_type: flyerType,
        flyer_weight: "",
        total_cost: convertNumber({
          number: data.grand,
          currency: currencySign,
          fixed: 2,
        }),
      });
      setGtagEventSent(true);
    }
  }, [isModalOpen]);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setLoading(false);
  };

  const getFlyerInfo = async () => {
    const data = await getCampaignFlyerData(campaignId);
    window.open(data?.campaignFlyerInfo?.s3Link || "", "_blank").focus();
  };

  const errorOnSubmit = (error) => {
    runProgressBar(0);
    runSnackBar({
      type: "error",
      msg: `Server error failed to submit company. ${error.message}`,
      vertical: "bottom",
      horizontal: "right",
    });

    handleCloseModal();
  };

  const headerActions = {
    BACK: {
      action: async () => {
        if (internalStatus === CAMPAIGN_INTERNAL_STATUSES.DRAFT || !internalStatus) {
          // Go back to Design page (which now includes tracking/details)
          history.push(generatePath(EDDM_ROUTING.EDDM_FLYER, { campaignId, clientId }));
        } else {
          history.push(generatePath(EDDM_ROUTING.EDDM_CAMPAIGN_SEGMENTS, { campaignId, clientId }));
        }
      },
    },
  };

  const handleDatesChange = () => history.push(generatePath(EDDM_ROUTING.EDDM_FLYER, { campaignId, clientId }));

  const handlePrint = useReactToPrint({
    content: () => {
      const print = document.createElement("div");

      if (firstPageRef.current) {
        const page = createPrintingPage(firstPageRef);
        print.appendChild(page);
      }

      if (secondPageRef.current) {
        const page = createPrintingPage(secondPageRef);
        print.appendChild(page);
      }

      return print;
    },
  });

  const sideEffects = async ({ saveLogs = false, doCheckout = false }) => {
    setIsSubmitConfirmationModalOpen(true);

    if (doCheckout) {
      try {
        // Fetch cart data when checkout is needed
        await doCheckoutGQL({
          campaignId,
          campaignDetailedCosts: {
            ...campaignDetailedCosts,
            tax: toFixed2(tax),
            totalCost: toFixed2(totalCost),
            amountOff: amountOff || 0,
            percentOff: percentOff || 0,
          },
        });
      } catch (error) {
        console.error("Failed to fetch cart data:", error);
        runSnackBar({
          type: "error",
          msg: "Failed to fetch cart information.",
          vertical: "bottom",
          horizontal: "right",
        });
      }
    }

    await updateCampaignExtraData({
      campaignId,
      paymentStatus: "uncaptured",
      internalStatus: CAMPAIGN_INTERNAL_STATUSES.IN_REVIEW,
      lastActiveStep: "submit",
      subtype: CAMPAIGN_SUBTYPES_ENUM.EDDM,
    });
    updateCampaign({
      internalStatus: CAMPAIGN_INTERNAL_STATUSES.IN_REVIEW,
      isSubmitted: true,
      lastActiveStep: "submit",
    });
    runProgressBar(0);
    const promotionId = promotionIdRef.current || "";
    localStorage.setItem(`first_time_transaction_${promotionId}`, promotionId);
    savedStartDate &&
      gtagWrapper({
        event: "ss_submit_completed",
        client_id: clientId,
        user_id: userId,
        campaign_id: campaignId,
        launch_date: savedStartDate ? formatDate(new Date(savedStartDate), "MMM d, yyyy") : "No date available",
        nr_of_flyers: flyersAmount,
        flyer_type: flyerType,
        total_cost: convertNumber({
          number: data.grand,
          currency: currencySign,
          fixed: 2,
        }),
        firstname: user.firstName,
        lastname: user.lastName,
        phone: user.phone,
        email: user.email,
      });
    if (saveLogs) {
      await insertCampaignLogs([
        {
          campaignId,
          type: CAMPAIGN_LOG_ENUM_TYPES.CAMPAIGN_SUBMITTED,
          additionalInfo: `[CB] CAMPAIGN EDIT Subtotal: ${replaceNullValue(
            subtotal?.toFixed(2)
          )}, Amount off: ${replaceNullValue(amountOff)}, Percent off: ${replaceNullValue(
            percentOff
          )}, Total: ${replaceNullValue(Number(totalCost?.toFixed(2)))}`,
        },
        {
          campaignId,
          type: CAMPAIGN_LOG_ENUM_TYPES.SET_PAYMENT_STATUS,
          additionalInfo: `From campaign builder. Status: [uncaptured]`,
        },
      ]);

      await updateDetailedCosts();
    }
  };

  const updatePathfinderData = async () => {
    await updateSelfServeCampaignData({
      campaignId: campaignId,
      campaignDetailedCosts,
    });
  };

  const updateCampaignAndCampaignExtraData = async () => {
    const startDateValue = new Date(`${startDate.value}`);

    await updateCampaignDB({
      campaignId,
      // offerExpirationDate: new Date(`${endDate.value}`),
      name: campaignName.trim(),
      printingCostPerFlyer: costPerFlyer,
      startDate: startDateValue,
    });

    await updateCampaignExtraData({
      campaignId,
      stripeCouponCode,
      startDate: startDateValue,
      // offerExpiryDate: new Date(`${endDate.value}`),
      flyerType,
      totalCosts: totalCost,
      taxes: tax,
      submitterId: user.id,
      flyersCount: flyersAmount,
    });
  };

  const updateDetailedCosts = async () => {
    // Gets for DM and D2D/H2H (endpoint used for both)
    const detailedCost = await getEddmCampaignCost({ id: campaignId, cityId: city ? city.id : 0 });
    updateCostsCalculationData({
      detailedCost,
    });
  };

  const handleSubmit = async () => {
    // Refuse to submit a campaign priced at 0 (PAD-32595). `runSideEffect` writes
    // this total straight to pathfinder and CampaignExtraData, and nothing
    // server-side ever recomputes it — so a 0 here is the number ops and the
    // client's invoice end up seeing. Stripe used to reject it implicitly (it
    // cannot charge $0); skip-checkout has no such step, which is why only
    // skip-stripe campaigns ended up persisting the 0. Not routed through
    // errorOnSubmit: this is a validation failure, not a "Server error".
    // Checked on the pre-discount subtotal, not the total: a 100%-off coupon
    // legitimately drives the total to 0, and blocking that would refuse a valid
    // free campaign. A 0 subtotal only ever means the cost inputs did not load.
    if (!subtotal || subtotal <= 0) {
      runSnackBar({
        type: "error",
        msg: "The campaign cost is still 0 — please make sure the audience is selected and its data has loaded, then try again.",
        vertical: "bottom",
        horizontal: "right",
      });
      return;
    }

    try {
      if (stripeCouponCode) {
        if (!(await handleApplyCouponCode({ stripeCouponCode }))) {
          throw new Error("Sorry, this coupon code is not active any more.");
        }
      }
      runProgressBar(60);
      runProgressBar(70);
      await runSideEffect();
    } catch (error) {
      errorOnSubmit(error);
    }
  };

  const getGrandForStripe = () => {
    let grand = totalCost + amountOff;
    if (percentOff && percentOff < 100) {
      /**
       * on stripe side in case we applied percent off coupon
       * grand - grand * percentOff / 100 = totalCost
       */
      grand = (totalCost * 100) / (100 - percentOff);
    }

    return Math.trunc((Number(grand) * 100).toFixed(2));
  };

  const fetchStripeCheckout = async () => {
    const promotionId = promotionIdRef.current || "";
    // HUB-440 (EDDM review-for-discount): carry the client's "one discount per
    // order" intent on the legacy checkout. The payments service applies the
    // review discount when `discount_choice === "review"` and the client has an
    // active entitlement; `undefined` (flag off) is dropped by JSON.stringify
    // (zero-footprint). Same deriver ADM's useADMSubmit uses.
    const checkoutDiscountChoice = deriveCheckoutDiscountChoice({
      isReviewDiscountEnabled,
      discountChoice: reviewDiscountChoice,
      hasStripeCoupon: !!stripeCouponCode,
    });
    const res = await apiClient.embeddedCheckoutCampaign({
      campaignId,
      clientId: clientId,
      campaignName: `Campaign ${campaignName ? campaignName.trim() : campaignName}`,
      grand: getGrandForStripe(),
      currencyCode: getCurrencyCode(country),
      countryCode: country?.code,
      promotionId,
      customerId: customerIdRef.current || "",
      userId,
      subtype,
      campaignDetailedCosts,
      discountChoice: checkoutDiscountChoice,
    });
    if (res.status === 200) {
      const { clientSecret } = await res.json();

      return clientSecret;
    }
  };

  const onSubmit = async () => {
    runProgressBar(40);
    setLoading(true);

    gtagWrapper({
      event: "ss_submit_tc_agreed",
      client_id: clientId,
      user_id: userId,
      campaign_id: campaignId,
      launch_date: formatDate(new Date(startDate.value), "MMM d, yyyy"),
      nr_of_flyers: flyersAmount,
      flyer_type: flyerType,
      total_cost: convertNumber({
        number: data.grand,
        currency: currencySign,
        fixed: 2,
      }),
    });

    await handleSubmit();
    setLoading(false);
  };

  const updateCouponCode = async ({ stripeCouponCode, amountOff, percentOff }) => {
    const { tax, totalCost } = calculateTaxAndTotalCost({ countryTaxRate, subtotal, amountOff });
    campaignDetailedCosts = {
      ...campaignDetailedCosts,
      amountOff: amountOff || 0,
      percentOff: percentOff || 0,
      tax,
      totalCost,
    };

    updateCampaign({ stripeCouponCode });
    updateCostsCalculationData({
      detailedCost: {
        ...detailedCost,
        tax: toFixed2(tax),
        totalCost: toFixed2(totalCost),
        amountOff,
        percentOff,
      },
    });
  };

  const getCustomerEmail = async (customer) => {
    try {
      const res = await apiClient.getCustomer({
        id: customer,
        countryCode: country?.code,
      });

      if (res.status === 200) {
        const { customer: responseData } = await res.json();

        if (responseData) {
          return responseData.email;
        }
      }

      return null;
    } catch (err) {
      return null;
    }
  };

  const handleApplyCouponCode = async ({ stripeCouponCode, saveLogs = false }) => {
    if (stripeCouponCode) {
      const code = stripeCouponCode.trim();
      try {
        const res = await apiClient.getPromotionCode({
          promotionCode: code,
          countryCode: country?.code,
        });
        if (res.status !== 200) {
          throw new Error(COUPON_CODE_ERROR_MESSAGE["DEFAULT"]);
        }
        const { promotionCodes: responseData } = await res.json();
        if (!responseData) {
          throw new Error(COUPON_CODE_ERROR_MESSAGE["DEFAULT"]);
        }

        const { data } = responseData;
        if (!data || !data[0] || code !== data[0].code) {
          throw new Error(COUPON_CODE_ERROR_MESSAGE["DEFAULT"]);
        }

        const {
          id: promoId,
          coupon: couponObject,
          max_redemptions: max_redemptions_promotion_code,
          times_redeemed: times_redeemed_promotion_code,
          customer,
          restrictions,
          expires_at,
        } = data[0];
        if (!couponObject && !couponObject.id) {
          throw new Error(COUPON_CODE_ERROR_MESSAGE["NO_PROMO_EXIST"]);
        }
        if (
          max_redemptions_promotion_code &&
          times_redeemed_promotion_code &&
          max_redemptions_promotion_code <= times_redeemed_promotion_code
        ) {
          throw new Error(COUPON_CODE_ERROR_MESSAGE["EXCEED_USE_PROMO"]);
        }

        const { max_redemptions: max_redemptions_coupon, times_redeemed: times_redeemed_coupon } = couponObject;
        if (max_redemptions_coupon && times_redeemed_coupon && max_redemptions_coupon <= times_redeemed_coupon) {
          throw new Error(COUPON_CODE_ERROR_MESSAGE["EXCEED_USE_COUPON"]);
        }

        if (customer) {
          const customerEmail = await getCustomerEmail(customer);

          if (customerEmail !== user.email) {
            throw new Error(COUPON_CODE_ERROR_MESSAGE["NO_PERMISSION"]);
          }
          customerIdRef.current = customer;
        }

        /**
         * if coupon is fixed discount and has different currency
         */
        if (
          couponObject.amount_off > 0 &&
          couponObject.currency &&
          couponObject.currency.toUpperCase() !== getCurrencyCode(country).toUpperCase()
        ) {
          throw new Error(COUPON_CODE_ERROR_MESSAGE["INVALID_CURRENCY"]);
        }

        if (couponObject.duration_in_months && couponObject.created) {
          const expiredDate = new Date(Number(couponObject.created) * 1000);
          expiredDate.setMonth(expiredDate.getMonth() + Number(couponObject.duration_in_months));
          if (expiredDate < new Date()) {
            throw new Error(COUPON_CODE_ERROR_MESSAGE["COUPON_EXPRIED"]);
          }
        }

        /**
         * this promo code has some restrictions such as 'minimum_amount'
         */
        if (restrictions) {
          const { minimum_amount, minimum_amount_currency, first_time_transaction } = restrictions;

          if (minimum_amount) {
            if (minimum_amount / 100 > subtotal) {
              throw new Error(COUPON_CODE_ERROR_MESSAGE["MINIMUM_ORDER"]);
            }

            if (
              minimum_amount > 0 &&
              minimum_amount_currency &&
              minimum_amount_currency.toUpperCase() !== getCurrencyCode(country).toUpperCase()
            ) {
              throw new Error(COUPON_CODE_ERROR_MESSAGE["INVALID_CURRENCY_IN_RESTRICTION"]);
            }
          }

          if (first_time_transaction) {
            // TO DO: SAVE THIS ON STRIPE CUSTOMER METADATA OR ON THE OPPIZI USER
            const didReedemed = !!localStorage.getItem(`first_time_transaction_${promoId}`);
            if (didReedemed) {
              throw new Error(COUPON_CODE_ERROR_MESSAGE["ONLY_FIRST"]);
            }
          }
        }

        if (expires_at) {
          if (new Date().getTime() / 1000 > expires_at) {
            throw new Error(COUPON_CODE_ERROR_MESSAGE["PROMO_EXPIRED"]);
          }
        }
        /**
         * if percent_off is a positive float larger than 0, and smaller or equal to 100,
         * that represents the discount the coupon will apply
         *
         * amount_off is a positive integer representing the amount to subtract from an invoice total
         *
         * amount_off = subtotal * percent_off / 100
         */
        let percent_off = null;
        let amount_off = null;
        let flag = false;

        if (couponObject.percent_off > 0 && couponObject.percent_off < 100) {
          percent_off = couponObject.percent_off;
          amount_off = (subtotal * percent_off) / 100;
          amount_off = Math.round((((subtotal * percent_off) / 100) * 100).toFixed(2)) / 100;
          flag = true;
        }
        if (couponObject.amount_off > 0 && couponObject.amount_off < subtotal * 100) {
          amount_off = Math.round(((couponObject.amount_off / 100) * 100).toFixed(2)) / 100;
          flag = true;
        }
        if (!flag) {
          throw new Error(COUPON_CODE_ERROR_MESSAGE["INCORRECT_DISCOUNT_VALUE"]);
        }

        if (subtotal - amount_off <= 0.5) {
          throw new Error(COUPON_CODE_ERROR_MESSAGE["LESS_THAN"]);
        }

        await updateCouponCode({
          stripeCouponCode: code,
          amountOff: amount_off,
          percentOff: percent_off,
        });

        const snackBarMessage = percent_off
          ? `Your ${percent_off}% discount has been successfully applied!`
          : `Your ${currencySign}${amount_off} discount has been successfully applied!`;

        runSnackBar({
          type: "success",
          vertical: "top",
          msg: snackBarMessage,
        });

        if (saveLogs) {
          await insertCampaignLogs([
            {
              campaignId,
              type: CAMPAIGN_LOG_ENUM_TYPES.SET_COUPON_CODE,
              additionalInfo: `[CB] CAMPAIGN EDIT Coupon:${replaceNullValue(code)}, Amount off: ${replaceNullValue(
                amount_off
              )}, Percent off: ${replaceNullValue(percent_off)}`,
            },
          ]);
        }

        promotionIdRef.current = promoId;
        setCouponErrorMessage("");
        return true;
      } catch (err) {
        setCouponErrorMessage(err.message);
      }
    }

    // in case invalid or empty code
    await updateCouponCode({
      stripeCouponCode: null,
      amountOff: null,
      percentOff: null,
    });
    promotionIdRef.current = "";
    customerIdRef.current = "";

    return false;
  };

  return isLoaded && Object.keys(data).length && !isLoading ? (
    <div className="flex h-screen w-full overflow-hidden">
      <EddmOadsSidebar country={country} client={client} />
      <main className="flex h-screen min-w-0 flex-1 flex-col overflow-hidden" data-copilot-step="EDDM Review">
        <Header
          tabs={EDDM_NAVIGATION}
          activeTabIndex={3}
          headerActions={headerActions}
          isSubmitted={isSubmitted}
          hideBackButton={
            internalStatus !== CAMPAIGN_INTERNAL_STATUSES.DRAFT &&
            internalStatus !== CAMPAIGN_INTERNAL_STATUSES.IN_REVIEW
          }
          isDM={true}
        />
        <QuoteView
          refs={{ firstPageRef, secondPageRef }}
          isPaymentSkipped={isPaymentSkipped}
          stripeCouponCode={stripeCouponCode}
          couponErrorMessage={couponErrorMessage}
          setCouponErrorMessage={setCouponErrorMessage}
          data={data}
          onSubmit={onSubmit}
          handleViewDesignClick={getFlyerInfo}
          dates={{ startDate }}
          timezone={timezone}
          handlePrint={handlePrint}
          toAudience={() => history.push(generatePath(EDDM_ROUTING.EDDM_CAMPAIGN_SEGMENTS, { campaignId, clientId }))}
          isModalOpen={isModalOpen}
          setIsModalOpen={setIsModalOpen}
          isSubmitConfirmationModalOpen={isSubmitConfirmationModalOpen}
          setIsSubmitConfirmationModalOpen={setIsSubmitConfirmationModalOpen}
          handleCloseModal={handleCloseModal}
          loading={loading}
          onApplyCouponCode={handleApplyCouponCode}
          detailedCost={costsCalculationData.detailedCost}
          reviewDiscount={reviewDiscount}
          handleDatesChange={handleDatesChange}
          pageState={pageState}
          campaignId={campaignId}
          flyerInfo={flyerInfo}
          reviewOfferComponent={() => (
            <EddmReviewOffer
              campaignId={campaignId}
              countryCode={country?.code}
              hasStripeCoupon={!!stripeCouponCode}
              onClearCoupon={() => updateCouponCode({ stripeCouponCode: null, amountOff: null, percentOff: null })}
              onDiscountChoiceChange={setReviewDiscountChoice}
              onEntitlementChange={setReviewEntitlement}
            />
          )}
          checkoutComponent={() => (
            <CheckoutContent
              campaignId={String(campaignId)}
              clientId={String(clientId)}
              breakdown={costBreakdown}
              breakdownReady={breakdownReady}
              couponCode={stripeCouponCode || undefined}
              applyReviewDiscount={applyReviewDiscount}
              // Do not default to "US" — propagating
              // undefined keeps Stripe.js from loading the US key for a
              // non-US client while `country` is still loading, which
              // routed confirms to the wrong Stripe account.
              countryCode={country?.code}
              onClose={() => setShowCheckout(false)}
              onPaymentSuccess={() => {
                /* Campaign status updated by Temporal workflow */
              }}
            />
          )}
          showCheckout={showCheckout}
          onCloseCheckout={() => setShowCheckout(false)}
        />
      </main>
    </div>
  ) : (
    <Box display="flex" alignItems="center" justifyContent="center" width="100%" height="100vh">
      <BounceLoader />
    </Box>
  );
};
